// Catalog browsing state for the Request Access modal — flattens the nested
// catalog response into request rows, then applies the search + two filter
// dropdowns. Pure derivation over `catalogs`; owns no submit/basket concerns.

import { useMemo, useState } from "react";
import type { MFAccessCatalog } from "state/members/access_v2/catalogs";
import { catalogLinkTenant } from "state/members/access_v2/catalogNames";
import { flattenCatalogs, type FlatRequestItem } from "./types";

interface IdName {
  id: string;
  name: string;
}

export interface CatalogBrowse {
  /** Every (catalog → item → resource) row, flattened. */
  flat: FlatRequestItem[];
  catalogNames: IdName[];
  /** Catalog items within the selected catalog (or all when none selected). */
  appsForCurrentCatalog: IdName[];
  filteredItems: FlatRequestItem[];
  search: string;
  setSearch: (v: string) => void;
  catalogFilter: string | null;
  appFilter: string | null;
  /** Selecting a catalog clears any stale app filter from the previous one. */
  selectCatalog: (id: string | null) => void;
  setAppFilter: (id: string | null) => void;
}

export interface CatalogFilters {
  search: string;
  catalogFilter: string | null;
  appFilter: string | null;
}

// Matches every name a card shows: its title, its app, and its tenant line.
const matchesSearch = (it: FlatRequestItem, q: string): boolean =>
  [it.resource.name, it.catalogItem.name, catalogLinkTenant(it.resource)].some(
    (text) => text?.toLowerCase().includes(q),
  );

export const filterCatalogItems = (
  flat: FlatRequestItem[],
  { search, catalogFilter, appFilter }: CatalogFilters,
): FlatRequestItem[] => {
  const q = search.toLowerCase();
  return flat.filter((it) => {
    if (catalogFilter && it.catalogId !== catalogFilter) return false;
    if (appFilter && it.catalogItemId !== appFilter) return false;
    return !q || matchesSearch(it, q);
  });
};

export const useCatalogBrowse = (
  catalogs: MFAccessCatalog[],
  initial: { catalogId?: string | null; catalogItemId?: string | null },
): CatalogBrowse => {
  const flat = useMemo(() => flattenCatalogs(catalogs), [catalogs]);

  const [search, setSearch] = useState("");
  const [catalogFilter, setCatalogFilter] = useState<string | null>(
    initial.catalogId ?? null,
  );
  const [appFilter, setAppFilter] = useState<string | null>(
    initial.catalogItemId ?? null,
  );

  const catalogNames = useMemo<IdName[]>(
    () => catalogs.map((c) => ({ id: c.id, name: c.name })),
    [catalogs],
  );

  const appsForCurrentCatalog = useMemo<IdName[]>(() => {
    const seen = new Map<string, string>();
    flat.forEach((it) => {
      if (catalogFilter && it.catalogId !== catalogFilter) return;
      seen.set(it.catalogItemId, it.catalogItem.name);
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [flat, catalogFilter]);

  const filteredItems = useMemo(
    () => filterCatalogItems(flat, { search, catalogFilter, appFilter }),
    [flat, search, catalogFilter, appFilter],
  );

  return {
    flat,
    catalogNames,
    appsForCurrentCatalog,
    filteredItems,
    search,
    setSearch,
    catalogFilter,
    appFilter,
    selectCatalog: (id) => {
      setCatalogFilter(id);
      setAppFilter(null);
    },
    setAppFilter,
  };
};
